/**
 * Backend neural TTS adapter (#93, D-TTS / D-FALL).
 *
 * `PollySynthesizer` implements the same {@link SpeechSynthesizer} seam as the browser
 * {@link WebSpeechSynthesizer}, so the pages/hooks are unchanged (D-SEAM). `speakClue`
 * POSTs the resolved {@link SpeechSegment}s (plus the current game ticket) to
 * `POST /api/voice/tts`; the server builds SSML, calls Polly neural, and streams back
 * `audio/mpeg`, which this plays through an `<audio>` element and resolves on `ended`.
 *
 * Every failure path — fetch error, non-2xx, or a playback error — falls back to a
 * `WebSpeechSynthesizer` so a client's Polly hiccup never breaks the read (D-FALL). The
 * server still opens the buzzer on its own duration estimate, so play never stalls.
 *
 * `fetch`, the audio-element factory, and the fallback are all injectable (mirroring
 * `WebSpeechSynthesizer`'s injectable style) so unit tests drive it without a network
 * or real Web Audio.
 */

import type { SpeechSegment } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { getVoiceTicket } from './voice-config';
import { WebSpeechSynthesizer, type SpeechSynthesizer } from './speech-synth';

/** Structural subset of the `fetch` Response we read. */
export interface TtsResponseLike {
  ok: boolean;
  status: number;
  blob(): Promise<Blob>;
}

export type TtsFetch = (
  url: string,
  init: {
    method: string;
    headers: Record<string, string>;
    // Omitted for a GET (fetching pre-generated audio, #99) — a GET/HEAD request
    // may not carry a body.
    body?: string;
  },
) => Promise<TtsResponseLike>;

/** Structural subset of `HTMLAudioElement` we drive (injectable for tests). */
export interface AudioElementLike {
  src: string;
  onended: (() => void) | null;
  onerror: (() => void) | null;
  play(): Promise<void> | void;
  pause(): void;
}

export type AudioElementFactory = () => AudioElementLike;

export interface PollySynthesizerOptions {
  /** Defaults to `globalThis.fetch`. Injectable for tests. */
  fetchImpl?: TtsFetch;
  /** Audio-element constructor. Defaults to `new Audio()`. Injectable for tests. */
  createAudio?: AudioElementFactory;
  /** The browser synthesizer used when the backend fails (D-FALL). */
  fallback?: SpeechSynthesizer;
  /** Blob → playable object URL. Defaults to `URL.createObjectURL`. */
  createObjectUrl?: (blob: Blob) => string;
  /** Revoke a created object URL. Defaults to `URL.revokeObjectURL`. */
  revokeObjectUrl?: (url: string) => void;
  /** Supplies the game ticket for auth. Defaults to {@link getVoiceTicket}. */
  ticketProvider?: () => string | null;
  /** HTTP base for `/api/voice/tts`. Defaults to {@link SERVER_HTTP_URL}. */
  baseUrl?: string;
}

function resolveGlobalFetch(): TtsFetch {
  const g = globalThis as { fetch?: (input: string, init?: unknown) => Promise<unknown> };
  if (typeof g.fetch !== 'function') {
    // No fetch (very old browser / bare test env): every call routes to the fallback.
    return () => Promise.reject(new Error('fetch unavailable'));
  }
  return g.fetch.bind(globalThis) as unknown as TtsFetch;
}

function defaultCreateAudio(): AudioElementLike {
  const Ctor = (globalThis as { Audio?: new () => AudioElementLike }).Audio;
  if (!Ctor) throw new Error('Audio element unavailable');
  return new Ctor();
}

function defaultCreateObjectUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

function defaultRevokeObjectUrl(url: string): void {
  URL.revokeObjectURL(url);
}

/** Backend {@link SpeechSynthesizer} over `POST /api/voice/tts` (Polly neural). */
export class PollySynthesizer implements SpeechSynthesizer {
  private readonly fetchImpl: TtsFetch;
  private readonly createAudio: AudioElementFactory;
  private readonly fallback: SpeechSynthesizer;
  private readonly createObjectUrl: (blob: Blob) => string;
  private readonly revokeObjectUrl: (url: string) => void;
  private readonly ticketProvider: () => string | null;
  private readonly baseUrl: string;

  /** Serialize reads so a newer clue supersedes a lagging previous one. */
  private generation = 0;
  private activeAudio: AudioElementLike | null = null;
  /** Resolver for the in-flight playback, so `cancel()` never leaves it hanging. */
  private activeResolve: (() => void) | null = null;

  constructor(opts: PollySynthesizerOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? resolveGlobalFetch();
    this.createAudio = opts.createAudio ?? defaultCreateAudio;
    this.fallback = opts.fallback ?? new WebSpeechSynthesizer();
    this.createObjectUrl = opts.createObjectUrl ?? defaultCreateObjectUrl;
    this.revokeObjectUrl = opts.revokeObjectUrl ?? defaultRevokeObjectUrl;
    this.ticketProvider = opts.ticketProvider ?? getVoiceTicket;
    this.baseUrl = opts.baseUrl ?? SERVER_HTTP_URL;
  }

  /** Backend TTS is always considered available; failures fall back per-read. */
  isSupported(): boolean {
    return true;
  }

  async speakClue(segments: SpeechSegment[]): Promise<void> {
    const gen = ++this.generation;
    try {
      const ticket = this.ticketProvider();
      const res = await this.fetchImpl(`${this.baseUrl}/api/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `ticket: undefined` is dropped by JSON.stringify → matches `{ segments, ticket? }`.
        body: JSON.stringify({ segments, ticket: ticket ?? undefined }),
      });
      if (!res.ok) throw new Error(`tts request failed (${res.status})`);
      const blob = await res.blob();
      if (gen !== this.generation) return; // superseded / cancelled while fetching
      await this.playBlob(blob);
    } catch {
      // D-FALL: any fetch/HTTP/play error → browser voice, so the read never breaks.
      if (gen !== this.generation) return;
      await this.fallback.speakClue(segments);
    }
  }

  /**
   * #99 (NFR-6.9): play pre-generated clue audio straight from its cached URL —
   * a single GET to CloudFront, no `/api/voice/tts` synthesis round-trip. Any
   * failure (fetch/HTTP/playback) falls back to {@link speakClue}, which POSTs for
   * on-demand synthesis and then to the browser voice (D-FALL), so a missing/stale
   * clip never breaks the read. The `ticket` auth isn't needed — `/audio/*` is
   * public, non-sensitive prompt audio served same-origin via CloudFront OAC.
   */
  async speakClueFromUrl(url: string, fallbackSegments: SpeechSegment[]): Promise<void> {
    const gen = ++this.generation;
    try {
      // `url` is the server-emitted same-origin `audioRef` (e.g. `/audio/v1/<hash>.mp3`),
      // fetched as-is — deployed, the web app, `/api`, and `/audio/*` share one
      // CloudFront origin, so no `baseUrl` prefix is needed (unlike the `/api/voice/tts`
      // POST in `speakClue`). In dev there is no CDN audio, so `audioRef` is never set.
      const res = await this.fetchImpl(url, { method: 'GET', headers: {} });
      if (!res.ok) throw new Error(`audio fetch failed (${res.status})`);
      const blob = await res.blob();
      if (gen !== this.generation) return; // superseded / cancelled while fetching
      await this.playBlob(blob);
    } catch {
      if (gen !== this.generation) return;
      // Pre-generated clip unavailable → on-demand synthesis (which itself falls
      // back to the browser voice). Re-reads speakClue's own generation guard.
      await this.speakClue(fallbackSegments);
    }
  }

  private playBlob(blob: Blob): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let url: string;
      try {
        url = this.createObjectUrl(blob);
      } catch (e) {
        reject(e instanceof Error ? e : new Error('object URL creation failed'));
        return;
      }
      const audio = this.createAudio();
      this.activeAudio = audio;
      const finish = (done: () => void): void => {
        if (this.activeAudio === audio) this.activeAudio = null;
        this.activeResolve = null;
        audio.onended = null;
        audio.onerror = null;
        try {
          this.revokeObjectUrl(url);
        } catch {
          /* best-effort cleanup */
        }
        done();
      };
      // A superseding read / cancel resolves (not rejects) so it counts as "done".
      this.activeResolve = () => finish(resolve);
      audio.onended = () => finish(resolve);
      audio.onerror = () => finish(() => reject(new Error('audio playback failed')));
      audio.src = url;
      const played = audio.play();
      if (played && typeof (played as Promise<void>).then === 'function') {
        (played as Promise<void>).catch(() =>
          finish(() => reject(new Error('audio play() rejected'))),
        );
      }
    });
  }

  /**
   * Prime an audio element within a user gesture (iOS unlock, D-TTS) and prime the
   * fallback engine too, since D-FALL may hand the read to it later. Best-effort.
   */
  unlock(): void {
    try {
      const audio = this.createAudio();
      const played = audio.play();
      if (played && typeof (played as Promise<void>).then === 'function') {
        (played as Promise<void>).then(() => audio.pause()).catch(() => {});
      } else {
        audio.pause();
      }
    } catch {
      /* best-effort priming */
    }
    this.fallback.unlock();
  }

  cancel(): void {
    this.generation++;
    const audio = this.activeAudio;
    if (audio) {
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
    }
    const resolve = this.activeResolve;
    this.activeResolve = null;
    if (resolve) resolve();
    this.fallback.cancel();
  }
}
